import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import Loader from 'components/Loader';
import React, { FunctionComponent } from 'react';

interface StudyManifestInProgressDialogProps {
  open: boolean;
  completedBatches: number;
  totalBatches: number;
  onClose: (e: React.MouseEvent<HTMLButtonElement>) => void;
  setRunningDialogOpen: (open: boolean) => void;
  failedBatches?: number;
  overrideFailed?: boolean;
  failedSchedulingCount?: number;
  onRetryFailedBatches?: () => void;
}

export const StudyManifestInProgressDialog: FunctionComponent<
  React.PropsWithChildren<StudyManifestInProgressDialogProps>
> = ({
  open,
  completedBatches,
  totalBatches,
  onClose,
  setRunningDialogOpen,
  failedBatches = 0,
  overrideFailed = false,
  failedSchedulingCount = 0,
  onRetryFailedBatches,
}) => {
  const settledBatches = completedBatches + failedBatches;
  const isFinished = overrideFailed || (totalBatches > 0 && settledBatches === totalBatches);
  const hasBatchFailures = failedBatches > 0;
  const hasSchedulingFailures = failedSchedulingCount > 0;
  const progressValue = totalBatches > 0 ? (settledBatches / totalBatches) * 100 : 0;
  const closeButtonLabel = hasBatchFailures || hasSchedulingFailures || overrideFailed ? 'Close' : 'Great!';

  return (
    <Dialog open={open}>
      <DialogContent>
        <Box aria-live="polite">
          {!isFinished ? (
            <Loader
              inline
              message={`Completed ${completedBatches} of ${totalBatches} execution steps.`}
              value={progressValue}
              ariaLabel="Slide organization progress"
            />
          ) : null}
          {isFinished && !hasBatchFailures && !hasSchedulingFailures && !overrideFailed ? (
            <Typography>Slide organization saved successfully.</Typography>
          ) : null}
          {overrideFailed ? (
            <Typography color="error">
              Whole-study replacement did not start because the existing cases could not be deleted. No case batches
              were applied. Close this dialog and investigate the request before retrying.
            </Typography>
          ) : null}
          {isFinished && hasBatchFailures ? (
            <Typography color="error">
              Completed {completedBatches} of {totalBatches} execution steps; {failedBatches}{' '}
              {failedBatches === 1 ? 'case batch failed' : 'case batches failed'}. Retry the failed batches or close
              this dialog and inspect the current Dataset state.
            </Typography>
          ) : null}
          {isFinished && !hasBatchFailures && hasSchedulingFailures ? (
            <Typography color="warning.main">
              Cases were saved, but automatic initial image analysis failed for {failedSchedulingCount}{' '}
              {failedSchedulingCount === 1 ? 'slide' : 'slides'}. Close this dialog, then use Run initial image analysis
              to retry or investigate the failed jobs.
            </Typography>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        {isFinished && hasBatchFailures && onRetryFailedBatches ? (
          <Button variant="outlined" color="primary" onClick={onRetryFailedBatches}>
            Retry failed batches
          </Button>
        ) : null}
        <Button
          variant="contained"
          color="primary"
          disabled={!isFinished}
          onClick={(e) => {
            setRunningDialogOpen(false);
            onClose(e);
          }}
        >
          {closeButtonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
